import React, { useEffect, useRef, useState } from "react";

import BlockIcon from "@mui/icons-material/Block";
import DoneIcon from "@mui/icons-material/Done";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { ReportGeneratorIcon } from "assets/material-custom-icons";
import { DataExtractionModal } from "components/Data-extraction/DataExtractionModal";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { EXTRACTABLE_DOCUMENT_TYPES } from "constants/index";
import { usePrompt } from "hooks";
import { useExtractionResultSocketHook } from "hooks/socket-hooks/use-extraction-result-socket-hook";
import { useAccountSelector } from "store";

/** Only the id is read off an upload result, so any file-ish response fits (IFile, IFileMetaDetail…). */
interface UploadedFileRef {
  id: number;
}

export interface UploadProgressItem {
  file: File;
  documentType: string | null;
  promise: Promise<UploadedFileRef | UploadedFileRef[]>;
  scanPromise?: Promise<boolean>;
}

interface Props {
  /** Contexts without AI extraction (global & client workspaces) — the column shows a "not supported" icon. */
  disableExtraction?: boolean;
  /** Which viewer the file-name link opens. */
  extractorSource?: "deal" | "globalWorkspace";
  items: UploadProgressItem[];
  isMinimized?: boolean;
  onScanCancel?: () => void;
}

type UploadState = "uploading" | "success" | "error";
type ScanState = "idle" | "pending" | "success" | "error";
type ExtractionState = "idle" | "pending" | "success" | "error" | "not-supported";
type ExtractableExtractionState = Exclude<ExtractionState, "not-supported">;

interface RowState {
  fileName: string;
  documentType: string | null;
  uploadState: UploadState;
  fileId?: number;
  scanState: ScanState;
  extractionState: ExtractionState;
  extractionErrorMessage?: string;
  isGlobalWorkspace?: boolean;
}

const isExtractable = (fileName: string, documentType: string | null) =>
  fileName.toLowerCase().endsWith(".pdf") && !!documentType && EXTRACTABLE_DOCUMENT_TYPES.includes(documentType);

const isPdf = (fileName: string) => fileName.toLowerCase().endsWith(".pdf");

export const UploadProgressPopover: React.FC<Props> = ({
  disableExtraction,
  extractorSource = "deal",
  items,
  isMinimized,
  onScanCancel
}) => {
  const openPrompt = usePrompt();
  const [isExtractorOpen, setIsExtractorOpen] = useState(false);
  const { show_recommendations, sanitize_documents } = useAccountSelector() || {};

  const onScanCancelRef = useRef(onScanCancel);
  onScanCancelRef.current = onScanCancel;

  useEffect(() => {
    return () => {
      onScanCancelRef.current?.();
    };
  }, []);

  const handleOpenExtractor = async (fileId: number, fileName: string) => {
    if (isExtractorOpen) return;
    setIsExtractorOpen(true);
    try {
      await openPrompt({
        componentType: "fullscreen",
        Component: DataExtractionModal,
        props: {
          fileIds: [fileId],
          fileNames: { [fileId]: fileName },
          source: extractorSource,
          initialShowParameters: false
        }
      });
    } finally {
      setIsExtractorOpen(false);
    }
  };
  const [rows, setRows] = useState<RowState[]>(() =>
    items.map((item) => ({
      fileName: item.file.name,
      documentType: item.documentType,
      uploadState: "uploading",
      scanState: "idle",
      extractionState: isExtractable(item.file.name, item.documentType) ? "idle" : "not-supported"
    }))
  );

  const isProcessing = rows.some(
    (row) => row.uploadState === "uploading" || row.scanState === "pending" || row.extractionState === "pending"
  );

  useEffect(() => {
    items.forEach((item, index) => {
      const extractable = isExtractable(item.file.name, item.documentType);
      const hasScan = !!item.scanPromise;
      item.promise
        .then((result) => {
          const file = Array.isArray(result) ? result[0] : result;
          setRows((prev) =>
            prev.map((row, i) =>
              i === index
                ? {
                    ...row,
                    uploadState: "success",
                    fileId: file?.id,
                    scanState: hasScan ? "pending" : "success",
                    extractionState: !extractable ? "not-supported" : !file?.id ? "idle" : hasScan ? "idle" : "pending"
                  }
                : row
            )
          );

          if (hasScan && item.scanPromise) {
            item.scanPromise.then((success) => {
              setRows((prev) =>
                prev.map((row, i) =>
                  i === index
                    ? {
                        ...row,
                        scanState: success ? "success" : "error",
                        extractionState: !extractable
                          ? "not-supported"
                          : success && row.fileId
                            ? "pending"
                            : row.extractionState
                      }
                    : row
                )
              );
            });
          }
        })
        .catch(() => {
          setRows((prev) => prev.map((row, i) => (i === index ? { ...row, uploadState: "error" } : row)));
        });
    });
  }, [items]);

  const handleExtractionResult = (index: number) => (success: boolean, message?: string) => {
    setRows((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, extractionState: success ? "success" : "error", extractionErrorMessage: message } : row
      )
    );
  };

  if (isMinimized) {
    return (
      <Stack direction="row" alignItems="center" gap="8px" sx={{ px: "16px", py: "10px" }}>
        {isProcessing ? <CircularProgress size={14} /> : <DoneIcon sx={{ color: "#07A27D", fontSize: 16 }} />}
        <Typography sx={{ fontSize: 12, color: "#496682" }}>
          {isProcessing ? "Processing" : "Completed"} {rows.length} file{rows.length === 1 ? "" : "s"}
        </Typography>
      </Stack>
    );
  }

  return (
    <Box sx={{ p: "16px" }}>
      <Stack direction="row" alignItems="center" gap="8px" sx={{ mb: "6px", px: "2px" }}>
        <Typography sx={{ flex: 1, fontSize: 11, fontWeight: 600, color: "#8596A8", textTransform: "uppercase" }}>
          Name
        </Typography>
        <Typography
          sx={{
            width: 56,
            fontSize: 11,
            fontWeight: 600,
            color: "#8596A8",
            textTransform: "uppercase",
            textAlign: "center"
          }}
        >
          Upload
        </Typography>
        {sanitize_documents && (
          <Typography
            sx={{
              width: 56,
              fontSize: 11,
              fontWeight: 600,
              color: "#8596A8",
              textTransform: "uppercase",
              textAlign: "center"
            }}
          >
            Scan
          </Typography>
        )}
        {show_recommendations && (
          <Typography
            sx={{
              width: 70,
              fontSize: 11,
              fontWeight: 600,
              color: "#8596A8",
              textTransform: "uppercase",
              textAlign: "center"
            }}
          >
            AI Extract
          </Typography>
        )}
      </Stack>
      <Stack gap="8px" sx={{ maxHeight: 320, overflowY: "auto" }}>
        {rows.map((row, index) => (
          <UploadProgressRow
            key={index}
            row={row}
            onExtractionResult={handleExtractionResult(index)}
            onOpenExtractor={handleOpenExtractor}
            extractorDisabled={isExtractorOpen}
            showExtraction={!!show_recommendations}
            showScan={!!sanitize_documents}
            disableExtraction={disableExtraction}
          />
        ))}
      </Stack>
    </Box>
  );
};

const UploadProgressRow: React.FC<{
  disableExtraction?: boolean;
  row: RowState;
  onExtractionResult: (success: boolean, message?: string) => void;
  onOpenExtractor: (fileId: number, fileName: string) => void;
  extractorDisabled: boolean;
  showExtraction: boolean;
  showScan: boolean;
}> = ({ disableExtraction, row, onExtractionResult, onOpenExtractor, extractorDisabled, showExtraction, showScan }) => {
  const isPdfFile = isPdf(row.fileName);
  const canOpen = isPdfFile && row.fileId != null && !extractorDisabled;

  return (
    <Stack direction="row" alignItems="center" gap="8px">
      <Typography
        noWrap
        onClick={canOpen ? () => onOpenExtractor(row.fileId!, row.fileName) : undefined}
        sx={{
          flex: 1,
          fontSize: 13,
          color: isPdfFile ? "#26a6ae" : "#496682",
          opacity: isPdfFile && extractorDisabled ? 0.5 : 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          cursor: canOpen ? "pointer" : "default",
          "&:hover": canOpen ? { textDecoration: "underline" } : undefined
        }}
      >
        {row.fileName}
      </Typography>
      <Stack sx={{ width: 56 }} alignItems="center">
        <UploadStatusIcon state={row.uploadState} />
      </Stack>
      {showScan && (
        <Stack sx={{ width: 56 }} alignItems="center">
          <ScanStatusIcon state={row.scanState} />
        </Stack>
      )}
      {showExtraction && (
        <Stack sx={{ width: 70 }} alignItems="center">
          {row.extractionState === "not-supported" || disableExtraction ? (
            <MaterialTooltip
              variant="dark"
              title={`Extraction is supported for PDF files and ${EXTRACTABLE_DOCUMENT_TYPES.join(", ")} Classifications`}
              isShow
            >
              <BlockIcon sx={{ color: "#7b92a9", fontSize: 18, flexShrink: 0 }} />
            </MaterialTooltip>
          ) : (
            <ExtractableRowIcon
              fileId={row.fileId}
              extractionState={row.extractionState}
              extractionErrorMessage={row.extractionErrorMessage}
              onExtractionResult={onExtractionResult}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
};

const ExtractableRowIcon: React.FC<{
  fileId?: number;
  extractionState: ExtractableExtractionState;
  extractionErrorMessage?: string;
  onExtractionResult: (success: boolean, message?: string) => void;
}> = ({ fileId, extractionState, extractionErrorMessage, onExtractionResult }) => {
  useExtractionResultSocketHook({
    fileId: extractionState === "pending" ? fileId : undefined,
    onResult: onExtractionResult
  });

  return <ExtractionStatusIcon state={extractionState} errorMessage={extractionErrorMessage} />;
};

const UploadStatusIcon: React.FC<{ state: UploadState }> = ({ state }) => {
  if (state === "uploading") return <CircularProgress size={16} />;
  if (state === "success") return <DoneIcon sx={{ color: "#07A27D", fontSize: "18px" }} />;
  return <ErrorOutlineIcon sx={{ color: "#D32F2F", fontSize: 18 }} />;
};

const ScanStatusIcon: React.FC<{ state: ScanState }> = ({ state }) => {
  if (state === "pending") return <CircularProgress size={16} />;
  const color = state === "success" ? "#07A27D" : state === "error" ? "#D32F2F" : "#C4C4C4";
  return <DoneIcon sx={{ color: color, fontSize: "18px" }} />;
};

const ExtractionStatusIcon: React.FC<{ state: ExtractableExtractionState; errorMessage?: string }> = ({
  state,
  errorMessage
}) => {
  if (state === "pending") return <CircularProgress size={16} />;
  if (state === "success") return <DoneIcon sx={{ color: "#07A27D", fontSize: "18px" }} />;
  if (state === "error")
    return (
      <MaterialTooltip variant="dark" title={errorMessage || "Extraction failed"} isShow>
        <ErrorOutlineIcon sx={{ color: "#D32F2F", fontSize: 18 }} />
      </MaterialTooltip>
    );
  return <ReportGeneratorIcon sx={{ color: "#C4C4C4", fontSize: 18, flexShrink: 0 }} />;
};
